<script setup>
import { computed, onMounted, ref } from 'vue';
import { RepositoryFactory } from '@/data/repositoryFactory.js';
import { isNotEmptyArray } from '../../../helpers/utilsHelper';
import DebugPanel from '../../test/DebugPanel.vue';
import ModalBase from '@/components/utils/ModalBase.vue';
import ModalBody from '@/components/utils/ModalBody.vue';
import LocationAdditionalDetails from './LocationAdditionalDetails.vue';
import SetPositionPanel from './SetPositionPanel.vue';

const MODAL_MODES = {
  SET_TYPE: 'set_type',
  SET_POSITION: 'set_position',
  CREATE_GROUP: 'create_group',
  SHOW_LOCATION_DETAILS: 'show_location_details'
};
const RepositoryDictionaries = RepositoryFactory.get('dictionaries');

const props = defineProps({ recordPoints: Array, noRecordPoints: Array, isLoading: Boolean });

const emit = defineEmits(['update:recordPoints', 'update:noRecordPoints']);

const demElemTypes = ref(null);
const modalBase = ref();
const setPositionPanelRef = ref();
const currentModalMode = ref(null);
const modalFormRef = ref();

const modalTemporaryDemElemType = ref(null);
const modalTemporaryNote = ref(null);
const modalLocationToShowDetails = ref(null);
const modalLocationSetPosition = ref(null);
let lastCreatedGroupId = 0;

const isLoadingForm = computed(() => {
  return !Array.isArray(demElemTypes.value);
});

const modalMaxWidth = computed(() => {
  switch (currentModalMode.value) {
    case MODAL_MODES.SET_TYPE:
      return '500px';
    case MODAL_MODES.SET_POSITION:
      return undefined;
    case MODAL_MODES.CREATE_GROUP:
      return undefined;
    case MODAL_MODES.SHOW_LOCATION_DETAILS:
      return '500px';
    default:
      return undefined;
  }
});

const dataAvailable = computed(() => {
  return isNotEmptyArray(props.recordPoints) || isNotEmptyArray(props.noRecordPoints);
});

const selectedLocations = computed(() => {
  return props.noRecordPoints.filter((location) => location.selected === true);
});

const selectedLocationsCount = computed(() => {
  return selectedLocations.value.length;
});

async function fetchDemElemTypes() {
  RepositoryDictionaries.getDictionaryElementsObject(1, 100, '', '', '', 'demelemtype')
    .then((data) => {
      demElemTypes.value = data?.data ?? [];
    })
    .catch((error) => console.log(error))
    .finally(() => {});
}

function showModal(mode) {
  modalTemporaryDemElemType.value = null;
  modalTemporaryNote.value = null;

  currentModalMode.value = mode;
  modalBase.value.show();
}

function onSetGlobalTypeBtnClicked() {
  if (!modalFormRef.value?.reportValidity()) return;

  if (Array.isArray(props.recordPoints)) {
    props.recordPoints.forEach((point) => {
      point.id_dem_elem_type = modalTemporaryDemElemType.value;
    });
    emit('update:recordPoints', props.recordPoints);
  }

  if (Array.isArray(props.noRecordPoints)) {
    props.noRecordPoints.forEach((point) => {
      if (point.id_group != null) return; //skip grouped points
      point.id_dem_elem_type = modalTemporaryDemElemType.value;
    });
    emit('update:noRecordPoints', props.noRecordPoints);
  }

  modalBase.value.hide();
}

function onSetPositionBtnClicked(location) {
  modalLocationSetPosition.value = location;
  showModal(MODAL_MODES.SET_POSITION);
}

function onConfirmLocationBtnClicked() {
  const newPosition = setPositionPanelRef.value?.currentPosition;

  modalLocationSetPosition.value.sz_geo = newPosition?.lat ?? modalLocationSetPosition.value.sz_geo;
  modalLocationSetPosition.value.dl_geo = newPosition?.lon ?? modalLocationSetPosition.value.dl_geo;

  modalBase.value.hide();
}

function onCreateGroupBtnClicked() {
  if (selectedLocationsCount.value === 0) return;

  modalLocationSetPosition.value = null;
  showModal(MODAL_MODES.CREATE_GROUP);
}

function onConfirmGroupBtnClicked() {
  if (!modalFormRef.value?.reportValidity()) return;

  const newDemElemTypeId = modalTemporaryDemElemType.value;
  const newNote = modalTemporaryNote.value;
  const newPosition = setPositionPanelRef.value?.currentPosition;

  if (selectedLocationsCount.value === 0) return;

  lastCreatedGroupId += 1;

  selectedLocations.value.forEach((location) => {
    location.id_group = lastCreatedGroupId;
    location.id_dem_elem_type = newDemElemTypeId;
    location.sz_geo = newPosition?.lat;
    location.dl_geo = newPosition?.lon;
    location.note = newNote;
  });

  selectedLocations.value.forEach((location) => {
    location.selected = false;
  });

  emit('update:noRecordPoints', props.noRecordPoints);

  modalBase.value.hide();
}

function resetGroups() {
  if (!isNotEmptyArray(props.noRecordPoints)) return;

  props.noRecordPoints.forEach((location) => {
    location.id_group = null;
    location.selected = false;
  });

  lastCreatedGroupId = 0;

  emit('update:noRecordPoints', props.noRecordPoints);
}

function onModalOpened() {
  if (
    currentModalMode.value === MODAL_MODES.SET_POSITION ||
    currentModalMode.value === MODAL_MODES.CREATE_GROUP
  ) {
    setPositionPanelRef.value?.initialize();
  }
}

onMounted(() => {
  fetchDemElemTypes();
});
</script>

<template>
  <div class="ibox">
    <div
      class="ibox-title"
      style="
        padding: 0px;
        padding-inline: 15px;
        display: flex;
        gap: 5px;
        justify-content: space-between;
        flex-wrap: wrap;
        align-items: center;
      "
    >
      <h5 style="margin-bottom: 0px">Lokalizacje</h5>

      <div style="display: flex; gap: 5px; align-items: center">
        <button
          type="button"
          class="btn btn-primary btn-xs"
          @click="showModal(MODAL_MODES.SET_TYPE)"
        >
          Ustaw typ
        </button>

        <button
          type="button"
          class="btn btn-primary btn-xs"
          :disabled="selectedLocationsCount === 0"
          @click="onCreateGroupBtnClicked"
        >
          Utwórz punkt zbiorczy
          {{ selectedLocationsCount > 0 ? '(' + selectedLocationsCount + ')' : '' }}
        </button>

        <button type="button" class="btn btn-primary btn-xs" @click="resetGroups">
          Resetuj grupy
        </button>
      </div>
    </div>

    <div class="ibox-content" :class="{ 'sk-loading': props.isLoading || isLoadingForm }">
      <div class="sk-spinner sk-spinner-three-bounce">
        <div class="sk-bounce1"></div>
        <div class="sk-bounce2"></div>
        <div class="sk-bounce3"></div>
      </div>

      <div v-if="dataAvailable" class="table-responsive mb-3 ml-auto mr-auto">
        <table class="table table-striped">
          <thead>
            <tr>
              <th></th>
              <th>Oznaczenie (NOSS)</th>
              <th>XID</th>
              <th>GEO_SZ</th>
              <th>GEO_DŁ</th>
              <th></th>
              <th>Ewidencja</th>
              <th>Typ</th>
              <th>Uwagi</th>
              <th>Informacje</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(location, index) in props.recordPoints" :key="index">
              <td></td>
              <td style="white-space: pre">{{ location.nazwa_noss ?? '-' }}</td>

              <td>
                <input type="text" v-model="location.xid" class="form-control" />
              </td>

              <td>
                <input
                  type="number"
                  step="any"
                  v-model="location.sz_geo"
                  class="form-control"
                  required
                />
              </td>

              <td>
                <input
                  type="number"
                  step="any"
                  v-model="location.dl_geo"
                  class="form-control"
                  required
                />
              </td>

              <td>
                <button
                  type="button"
                  class="btn btn-white"
                  style="height: 35px; width: 35px"
                  title="Ustaw pozycję"
                  @click="onSetPositionBtnClicked(location)"
                >
                  <i class="fa fa-map-marker"></i>
                </button>
              </td>

              <td>TAK</td>

              <td>
                <select class="form-control m-b" v-model="location.id_dem_elem_type" required>
                  <option v-for="type in demElemTypes" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </select>
              </td>

              <td>
                <textarea
                  style="min-width: 250px"
                  placeholder="Wpisz uwagi"
                  v-model="location.note"
                  class="form-control"
                  maxlength="500"
                ></textarea>
              </td>

              <td>
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-primary btn-outline"
                    style="height: 35px; width: 35px"
                    @click="
                      () => {
                        modalLocationToShowDetails = location;
                        showModal(MODAL_MODES.SHOW_LOCATION_DETAILS);
                      }
                    "
                  >
                    <i class="fa fa-info"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-for="(location, index) in props.noRecordPoints" :key="index">
              <td>
                <input
                  type="checkbox"
                  :id="'locationSelectedInputId-' + index"
                  style="width: 17px; height: 17px"
                  name="scales"
                  v-model="location.selected"
                  :disabled="location.id_group != null"
                />
              </td>

              <td style="white-space: pre">
                {{ location.nazwa_noss ?? '-'
                }}{{ location.id_group != null ? ' (grupa ' + location.id_group + ')' : '' }}
              </td>

              <td>
                <input
                  type="text"
                  v-model="location.xid"
                  class="form-control"
                  :disabled="location.id_group != null"
                />
              </td>

              <td>
                <input
                  type="number"
                  step="any"
                  v-model="location.sz_geo"
                  class="form-control"
                  required
                  :disabled="location.id_group != null"
                />
              </td>

              <td>
                <input
                  type="number"
                  step="any"
                  v-model="location.dl_geo"
                  class="form-control"
                  required
                  :disabled="location.id_group != null"
                />
              </td>

              <td>
                <button
                  type="button"
                  class="btn btn-white"
                  style="height: 35px; width: 35px"
                  title="Ustaw pozycję"
                  :disabled="location.id_group != null"
                  @click="onSetPositionBtnClicked(location)"
                >
                  <i class="fa fa-map-marker"></i>
                </button>
              </td>

              <td>NIE</td>

              <td>
                <select
                  class="form-control m-b"
                  v-model="location.id_dem_elem_type"
                  required
                  :disabled="location.id_group != null"
                >
                  <option v-for="type in demElemTypes" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </select>
              </td>

              <td>
                <textarea
                  style="min-width: 250px"
                  placeholder="Wpisz uwagi"
                  v-model="location.note"
                  class="form-control"
                  maxlength="500"
                  :disabled="location.id_group != null"
                ></textarea>
              </td>

              <td>
                <div class="btn-group">
                  <button
                    type="button"
                    class="btn btn-primary btn-outline"
                    style="height: 35px; width: 35px"
                    @click="
                      () => {
                        modalLocationToShowDetails = location;
                        showModal(MODAL_MODES.SHOW_LOCATION_DETAILS);
                      }
                    "
                  >
                    <i class="fa fa-info"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else>
        <p class="text-center mt-3 mb-3">Brak elementów do wyświetlenia</p>
      </div>
    </div>
  </div>

  <DebugPanel>
    <pre>{{ demElemTypes }}</pre>
  </DebugPanel>

  <Teleport to="#app">
    <ModalBase ref="modalBase" :maxWidth="modalMaxWidth" @opened="onModalOpened">
      <ModalBody>
        <template v-slot:title>
          <span v-if="currentModalMode === MODAL_MODES.SET_TYPE">Ustaw typ</span>
          <span v-else-if="currentModalMode === MODAL_MODES.SET_POSITION">Ustaw pozycję</span>
          <span v-else-if="currentModalMode === MODAL_MODES.CREATE_GROUP">Utwórz grupę</span>
          <span v-else-if="currentModalMode === MODAL_MODES.SHOW_LOCATION_DETAILS">
            Szczegóły lokalizacji {{ modalLocationToShowDetails?.nazwa_noss ?? '' }}
          </span>
        </template>

        <template v-slot:body>
          <form ref="modalFormRef" @submit.prevent>
            <div
              v-if="
                currentModalMode === MODAL_MODES.SET_TYPE ||
                currentModalMode === MODAL_MODES.CREATE_GROUP
              "
            >
              <select class="form-control m-b" v-model="modalTemporaryDemElemType" required>
                <option disabled :value="null" label="Wybierz typ"></option>
                <option v-for="type in demElemTypes" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>

            <div v-if="currentModalMode === MODAL_MODES.SET_TYPE">
              Uwaga! Wybrany typ zostanie ustawiony dla wszystkich lokalizacji, oprócz tych dodanych
              do grup.
            </div>

            <div v-if="currentModalMode === MODAL_MODES.SHOW_LOCATION_DETAILS">
              <LocationAdditionalDetails :location="modalLocationToShowDetails" />
            </div>

            <div v-if="currentModalMode === MODAL_MODES.CREATE_GROUP" class="m-b">
              <textarea
                style="min-width: 250px"
                placeholder="Wpisz uwagi"
                v-model="modalTemporaryNote"
                class="form-control"
                maxlength="500"
              ></textarea>
            </div>

            <div
              v-if="
                currentModalMode === MODAL_MODES.SET_POSITION ||
                currentModalMode === MODAL_MODES.CREATE_GROUP
              "
            >
              <SetPositionPanel
                ref="setPositionPanelRef"
                :initial-lat="modalLocationSetPosition?.sz_geo"
                :initial-lon="modalLocationSetPosition?.dl_geo"
              />
            </div>
          </form>
        </template>

        <template v-slot:footer>
          <button type="button" class="btn btn-outline btn-primary" @click="modalBase.hide">
            Zamknij
          </button>

          <button
            v-if="currentModalMode === MODAL_MODES.SET_TYPE"
            type="button"
            class="btn btn-primary"
            @click="onSetGlobalTypeBtnClicked"
          >
            Zastosuj do wszystkich
          </button>

          <button
            v-if="currentModalMode === MODAL_MODES.SET_POSITION"
            type="button"
            class="btn btn-primary"
            @click="onConfirmLocationBtnClicked"
          >
            Zatwierdź
          </button>

          <button
            v-if="currentModalMode === MODAL_MODES.CREATE_GROUP"
            type="button"
            class="btn btn-primary"
            @click="onConfirmGroupBtnClicked"
          >
            Utwórz punkt zbiorczy
          </button>
        </template>
      </ModalBody>
    </ModalBase>
  </Teleport>
</template>

<style scoped>
td {
  vertical-align: middle !important;
}

.table-responsive td:nth-child(3),
.table-responsive th:nth-child(3),
.table-responsive td:nth-child(4),
.table-responsive th:nth-child(4),
.table-responsive td:nth-child(5),
.table-responsive th:nth-child(5) {
  min-width: 200px;
}

.table-responsive td:nth-child(7),
.table-responsive th:nth-child(7) {
  min-width: 200px;
}

.table-responsive td:nth-child(8),
.table-responsive th:nth-child(8) {
  min-width: 200px;
}
</style>
